// ts-rest contract for the status of the automatic feeds.
//
// The vendors put their daily files in the landing bucket, and the S3 feed
// poller reads each new version of each file and ingests it. Butterfield sends
// its daily download (holdings and account balances) each business morning.
// Zack Sanford asked on 2026-10-20 how the Ops team can know that the file of
// the day reached Septima. This read gives, for each automatic source, the
// last file, when the bucket received it, when Septima read it, the outcome,
// and the history of the last days.
//
// Endpoints:
//   GET /api/automatic-feeds — the status of each automatic source

import { initContract } from '@ts-rest/core';
import { z } from 'zod';
import { errorBodyWithCode as errorBody } from '../schemas/common';

const c = initContract();

/** What the poller did with one version of one file. These are values. */
export const automaticFeedOutcomeSchema = z.enum(['ingested', 'skipped', 'failed']);
export type AutomaticFeedOutcome = z.infer<typeof automaticFeedOutcomeSchema>;

/** The state of the source for the London day of today. */
export const automaticFeedTodaySchema = z.enum(['ingested', 'skipped', 'failed', 'not_received']);
export type AutomaticFeedToday = z.infer<typeof automaticFeedTodaySchema>;

/** One version of one file that the poller read. */
export const automaticFeedReceiptSchema = z.object({
    /** The key of the file in the bucket, for example `butterfield/DailyDownload.zip`. */
    objectKey: z.string(),
    /** When the bucket received the file (ISO). */
    receivedAt: z.string(),
    /** When Septima last read the file (ISO). */
    processedAt: z.string(),
    outcome: automaticFeedOutcomeSchema,
    /** One line for each file in the version: what the poller did with it. */
    detail: z.string(),
    /** The number of reads. A failed version is read again, up to a limit. */
    attempts: z.number().int().nonnegative(),
});
export type AutomaticFeedReceipt = z.infer<typeof automaticFeedReceiptSchema>;

/** The files that the bucket received on one London day. */
export const automaticFeedDaySchema = z.object({
    /** `YYYY-MM-DD`, the London calendar day. */
    day: z.string(),
    receipts: z.array(automaticFeedReceiptSchema),
});
export type AutomaticFeedDay = z.infer<typeof automaticFeedDaySchema>;

export const automaticFeedSourceSchema = z.object({
    /** The folder of the source in the bucket, for example `butterfield`. A value. */
    counterparty: z.string(),
    label: z.string(),
    /** True when the poller reads this source on this deployment. */
    active: z.boolean(),
    today: automaticFeedTodaySchema,
    /** The newest file that the bucket received, or null when none is on record. */
    last: automaticFeedReceiptSchema.nullable(),
    /** The last days, newest first. A day with no file has no receipt. */
    days: z.array(automaticFeedDaySchema),
});
export type AutomaticFeedSource = z.infer<typeof automaticFeedSourceSchema>;

export const automaticFeedsResponseSchema = z.object({
    /** False when the poller is off on this deployment (no bucket or no source). */
    pollerOn: z.boolean(),
    /** The London day of today, `YYYY-MM-DD`. */
    today: z.string(),
    sources: z.array(automaticFeedSourceSchema),
});
export type AutomaticFeedsResponse = z.infer<typeof automaticFeedsResponseSchema>;

export const automaticFeedsContract = c.router(
    {
        status: {
            method: 'GET',
            path: '/automatic-feeds',
            responses: { 200: automaticFeedsResponseSchema, 403: errorBody },
            summary:
                'The last file, the outcome and the history of the last days of each automatic feed',
        },
    },
    { pathPrefix: '/api', strictStatusCodes: false },
);
